<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCrmContactsAPI from 'dashboard/api/laisCrmContacts';
import { useMapGetter } from 'dashboard/composables/store';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import { leadContactLine, leadInitials } from '../utils/crmPageUtils';
import { trackCrmParityEvents } from '../utils/crmTracking';

const props = defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openLead']);
const accountId = useMapGetter('getCurrentAccountId');
const { t } = useI18n();

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 10;

const query = ref('');
const results = ref([]);
const meta = ref({});
const page = ref(1);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasSearched = ref(false);
const hasError = ref(false);
let searchTimer = null;
let searchRequestId = 0;

const resultCount = computed(() => results.value.length);
const hasMoreResults = computed(
  () => Number(meta.value?.count || 0) > results.value.length
);
const canClear = computed(() => Boolean(query.value));
const isEmpty = computed(
  () =>
    hasSearched.value &&
    !isLoading.value &&
    !hasError.value &&
    !resultCount.value
);
const showInitialHint = computed(
  () =>
    !hasSearched.value &&
    !isLoading.value &&
    !hasError.value &&
    !resultCount.value
);

const clearSearchTimer = () => {
  if (!searchTimer) return;
  clearTimeout(searchTimer);
  searchTimer = null;
};

const resetSearchState = () => {
  results.value = [];
  meta.value = {};
  page.value = 1;
  hasSearched.value = false;
  hasError.value = false;
  isLoading.value = false;
  isLoadingMore.value = false;
};

const search = async ({
  force = false,
  pageNumber = 1,
  append = false,
} = {}) => {
  const term = query.value.trim();
  clearSearchTimer();
  if (!term || (!force && term.length < MIN_SEARCH_LENGTH)) {
    searchRequestId += 1;
    resetSearchState();
    return;
  }

  searchRequestId += 1;
  const requestId = searchRequestId;
  if (append) isLoadingMore.value = true;
  else isLoading.value = true;
  hasError.value = false;
  hasSearched.value = true;
  try {
    const params = {
      include_contact_inboxes: 'false',
      page: String(pageNumber),
      per_page: String(SEARCH_PAGE_SIZE),
      sort: 'recent_activity',
      q: term,
    };
    const { data } = await LaisCrmContactsAPI.search(params);
    if (requestId !== searchRequestId) return;
    results.value = append
      ? [...results.value, ...(data.payload || [])]
      : data.payload || [];
    meta.value = data.meta || {};
    page.value = pageNumber;
    trackCrmParityEvents({
      accountId: accountId.value,
      names: [append ? 'crm_search_next_page_clicked' : 'crm_search_typed'],
      properties: {
        q_length: term.length,
        page: pageNumber,
        results_count: results.value.length,
      },
    });
  } catch {
    if (requestId !== searchRequestId) return;
    hasError.value = true;
    results.value = [];
  } finally {
    if (requestId === searchRequestId) {
      isLoading.value = false;
      isLoadingMore.value = false;
    }
  }
};

const loadMore = () => {
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_search_next_page_clicked'],
    properties: { page: page.value + 1 },
  });
  search({ force: true, pageNumber: page.value + 1, append: true });
};

const scheduleSearch = () => {
  searchRequestId += 1;
  clearSearchTimer();
  hasError.value = false;

  if (query.value.trim().length < MIN_SEARCH_LENGTH) {
    resetSearchState();
    return;
  }

  searchTimer = setTimeout(() => search(), SEARCH_DEBOUNCE_MS);
};

const clearSearch = () => {
  searchRequestId += 1;
  clearSearchTimer();
  query.value = '';
  resetSearchState();
};

const openLead = lead => {
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_search_result_clicked'],
    properties: { contact_id: lead?.id },
  });
  emit('openLead', lead);
  emit('close');
};

watch(query, scheduleSearch);
watch(
  () => props.show,
  value => {
    if (value) {
      trackCrmParityEvents({
        accountId: accountId.value,
        names: ['crm_search_page_viewed'],
      });
    } else {
      clearSearch();
    }
  }
);

onBeforeUnmount(() => {
  searchRequestId += 1;
  clearSearchTimer();
});
</script>

<template>
  <LaisDrawer
    :show="props.show"
    side="bottom"
    size="lg"
    :title="t('LEADS.SEARCH.TITLE')"
    @close="emit('close')"
  >
    <template #body>
      <form class="grid gap-4" @submit.prevent="search({ force: true })">
        <label class="grid gap-1">
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('LEADS.SEARCH.LABEL') }}
          </span>
          <span class="relative">
            <input
              v-model="query"
              type="search"
              class="h-11 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 pr-11 text-lais-body-sm text-lais-text"
              :placeholder="t('LEADS.FILTERS.SEARCH_PLACEHOLDER')"
            />
            <button
              v-if="canClear"
              type="button"
              class="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-full text-lais-text-muted transition hover:bg-lais-bg-muted hover:text-lais-text"
              :aria-label="t('LEADS.SEARCH.CLEAR')"
              @click="clearSearch"
            >
              <span class="i-lucide-x size-4" aria-hidden="true" />
            </button>
          </span>
        </label>
        <button
          type="submit"
          class="inline-flex w-fit items-center gap-2 rounded-lais bg-lais-primary px-4 py-2.5 text-lais-label-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isLoading || !query.trim()"
        >
          <span
            :class="
              isLoading
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-search'
            "
            class="size-4"
            aria-hidden="true"
          />
          {{ t('LEADS.SEARCH.ACTION') }}
        </button>
      </form>

      <div class="mt-5 grid gap-2">
        <div v-if="isLoading" class="grid gap-2">
          <div
            v-for="index in 4"
            :key="index"
            class="flex items-center gap-3 rounded-lais border border-lais-border-soft p-3"
          >
            <div class="size-9 animate-pulse rounded-full bg-lais-bg-muted" />
            <div class="grid flex-1 gap-2">
              <div class="h-4 w-44 animate-pulse rounded bg-lais-bg-muted" />
              <div class="h-3 w-32 animate-pulse rounded bg-lais-bg-muted" />
            </div>
          </div>
        </div>
        <p v-else-if="hasError" class="text-lais-body-sm text-lais-danger">
          {{ t('LEADS.SEARCH.ERROR') }}
        </p>
        <div v-else-if="isEmpty" class="grid gap-1 text-lais-body-sm">
          <p class="font-semibold text-lais-text">
            {{ t('LEADS.SEARCH.EMPTY_TITLE') }}
          </p>
          <p class="text-lais-text-muted">
            {{ t('LEADS.SEARCH.EMPTY_DESCRIPTION') }}
          </p>
        </div>
        <p
          v-else-if="showInitialHint"
          class="text-lais-body-sm text-lais-text-muted"
        >
          {{ t('LEADS.SEARCH.HINT') }}
        </p>
        <p
          v-if="resultCount"
          class="text-lais-label-sm font-semibold text-lais-text-muted"
        >
          {{ t('LEADS.SEARCH.COUNT', resultCount, { n: resultCount }) }}
        </p>
        <button
          v-for="lead in results"
          :key="lead.id"
          type="button"
          class="flex items-center gap-3 rounded-lais border border-lais-border-soft p-3 text-left transition hover:border-lais-primary"
          @click="openLead(lead)"
        >
          <span
            class="grid size-9 shrink-0 place-items-center rounded-full bg-lais-primary-50 font-redHatDisplay text-lais-label-sm font-semibold text-lais-primary"
          >
            {{ leadInitials(lead) }}
          </span>
          <span class="min-w-0">
            <span class="block truncate font-semibold text-lais-text">
              {{ lead.name || t('LEADS.QUICK_VIEW.ANONYMOUS') }}
            </span>
            <span class="block truncate text-lais-body-xs text-lais-text-muted">
              {{ leadContactLine(lead) || t('LEADS.QUICK_VIEW.NO_CONTACT') }}
            </span>
          </span>
        </button>
        <button
          v-if="hasMoreResults"
          type="button"
          class="mx-auto mt-2 inline-flex items-center gap-2 rounded-full border border-lais-border px-4 py-2 text-lais-label-sm font-semibold text-lais-text hover:border-lais-primary hover:text-lais-primary"
          :disabled="isLoadingMore"
          @click="loadMore"
        >
          <span
            :class="
              isLoadingMore
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-chevron-down'
            "
            class="size-4"
            aria-hidden="true"
          />
          {{ t('LEADS.REFERRALS.LOAD_MORE') }}
        </button>
      </div>
    </template>
  </LaisDrawer>
</template>
